/**
 * The client app's side of client chat, for the stand-in (clientAdapter.js):
 * the salons a client talks to, as the approved mockups show them. Studio
 * Nine has a deposit and a form waiting, Polished Nail Bar has offered free
 * times, and Brow and Lash Room sent a note before tomorrow's visit.
 */
const minsAgo = (n) => new Date(Date.now() - n * 60000).toISOString();
const DAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'June', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
const pad = (n) => (n < 10 ? `0${n}` : String(n));

function dayAt(days, hour, minute = 0) {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate() + days, hour, minute);
}
const short = (d) => `${DAY[d.getDay()]} ${d.getDate()}`;
const long = (d) => `${DAY[d.getDay()]} ${d.getDate()} ${MONTH[d.getMonth()]}`;
const hm = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

export const SALONS = {
    'sal-s9': { id: 'sal-s9', name: 'Studio Nine', mark: 'S9', colour: '#29282D', replies: 'Usually replies within an hour', area: 'Hair and colour · Shoreditch' },
    'sal-pn': { id: 'sal-pn', name: 'Polished Nail Bar', mark: 'PN', colour: '#B4558F', replies: 'Usually replies within a few hours', area: 'Nails · Hackney' },
    'sal-bl': { id: 'sal-bl', name: 'Brow and Lash Room', mark: 'BL', colour: '#0E8A89', replies: 'Usually replies the same day', area: 'Brows and lashes · Islington' },
};

export const SALON_STAFF = [
    { id: 'st-sophie', name: 'Sophie Carter', salonId: 'sal-s9', role: 'Colourist' },
    { id: 'st-mia', name: 'Mia Lewis', salonId: 'sal-pn', role: 'Nail technician' },
    { id: 'st-tara', name: 'Tara Quinn', salonId: 'sal-bl', role: 'Brow and lash artist' },
];

/** The form a client fills in from the chat (Studio Nine's New client form). */
export const CLIENT_FORMS = {
    'f-new-client': {
        id: 'f-new-client',
        name: 'New client form',
        questions: [
            { id: 'q1', text: 'Is this your first visit to us?', type: 'choice', options: ['Yes', 'No'] },
            { id: 'q2', text: 'What would you like done?', type: 'text', placeholder: 'For example, a warm caramel balayage' },
            { id: 'q3', text: 'Have you ever had a reaction to hair dye?', type: 'choice', options: ['Yes', 'No', 'Not sure'], more: { when: 'Yes', label: 'Tell us what happened' }, flag: 'Yes' },
            { id: 'q4', text: 'Are you pregnant or breastfeeding?', type: 'choice', options: ['Yes', 'No', 'Prefer not to say'] },
            { id: 'q5', text: 'Any allergies or skin conditions we should know about?', type: 'text', placeholder: 'Leave blank if none', optional: true },
            { id: 'q6', text: 'Can we share photos of your hair on our socials?', type: 'choice', options: ['Yes', 'No'] },
        ],
    },
};

/** Times a salon offers, a day or three from now. */
export function offeredTimes() {
    return [dayAt(1, 10), dayAt(2, 11, 30), dayAt(3, 14)].map((d) => ({ day: short(d), time: hm(d), when: `${short(d)}, ${hm(d)}`, at: d.toISOString() }));
}

export function seedSalons(meId) {
    const m = (id, conversationId, authorId, ago, text, extra = {}) => ({
        id, conversationId, authorId, createdAt: minsAgo(ago), text, attachments: [], reactions: {}, mentions: [], ...extra,
    });
    const visit = dayAt(4, 14);
    const lashes = dayAt(1, 16);
    const conv = (id, salonId, staffId, extra) => ({
        id, space: 'salons', kind: 'salon', salonId, memberIds: [meId, staffId], pinned: false, muted: false,
        createdAt: minsAgo(60 * 24 * 60), readUpTo: {}, ...extra,
    });
    const conversations = {
        'c-s9': conv('c-s9', 'sal-s9', 'st-sophie', { nextVisit: `${long(visit)}, ${hm(visit)}` }),
        'c-pn': conv('c-pn', 'sal-pn', 'st-mia'),
        'c-bl': conv('c-bl', 'sal-bl', 'st-tara', { nextVisit: `${long(lashes)}, ${hm(lashes)}` }),
    };
    const messages = {
        'c-s9': [
            m('a1', 'c-s9', meId, 60 * 26, 'Is there parking near you?', { status: 'sent' }),
            m('a2', 'c-s9', 'st-sophie', 60 * 25, 'Yes, Park Lane is free after 6 and all day Sunday', { reactions: { '❤️': [meId] } }),
            m('a3', 'c-s9', meId, 52, "Could I add a toner this time? Here's the colour", {
                status: 'sent', attachments: [{ type: 'image', url: '', demoTone: ['#7a5a43', '#d9b48a'], title: 'Your photo', width: 640, height: 480 }],
            }),
            m('a4', 'c-s9', 'st-sophie', 46, "Of course! Toner added, you'll finish at 16:45", {
                card: { type: 'booking', id: 'demo-booking-s9', client: 'Booking updated', services: 'Balayage, Cut and finish, Toner', when: `${long(visit)}, 14:00 to 16:45`, stylist: 'Sophie', dayShort: DAY[visit.getDay()].toUpperCase(), dayNum: visit.getDate() },
            }),
            m('a5', 'c-s9', 'st-sophie', 12, "We take a £30 deposit to hold it, and there's a quick form before your visit", {
                card: { type: 'deposit', amount: 30, for: `Balayage · ${long(visit)}, 14:00`, status: 'requested' },
            }),
            m('a6', 'c-s9', 'st-sophie', 11, '', { card: { type: 'form', formId: 'f-new-client', name: 'New client form', questions: 6, minutes: 2, status: 'sent' } }),
        ],
        'c-pn': [
            m('b1', 'c-pn', meId, 60 * 24 * 2 + 30, 'Hi! Do you have anything for gel infills this week?', { status: 'sent' }),
            m('b2', 'c-pn', 'st-mia', 60 * 24 * 2, 'Hi! Here are a few times. Tap one to book it', {
                card: { type: 'slots', service: 'Gel infills', stylist: 'Mia', duration: '1h', price: 35, options: offeredTimes() },
            }),
        ],
        'c-bl': [
            m('c1', 'c-bl', 'st-tara', 60 * 3, `See you for your lash lift tomorrow at ${hm(lashes)}. Come with clean lashes, no mascara please 😊`),
            m('c2', 'c-bl', meId, 60 * 3 - 20, 'Will do, thank you!', { status: 'sent' }),
        ],
    };
    Object.keys(conversations).forEach((id) => {
        const list = messages[id];
        const last = list[list.length - 1];
        conversations[id].lastMessageAt = last.createdAt;
        // Studio Nine's deposit and form are new; the rest has been read.
        const mark = id === 'c-s9' ? minsAgo(40) : minsAgo(0);
        conversations[id].readUpTo = conversations[id].memberIds.reduce((acc, uid) => { acc[uid] = uid === meId ? mark : minsAgo(0); return acc; }, {});
    });
    return { conversations, messages };
}
